import { faClock, faEnvelope, faMessage, faUserGroup } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatCadence } from './scheduleFormat.ts';
import { withIcon } from '../../../core/components/Icon.tsx';
import SegmentedButton from '../../../core/components/SegmentedButton.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { displayName } from '../ProjectSharingFields.tsx';

const ChatIcon = withIcon(faMessage);
const SharedIcon = withIcon(faUserGroup);
const ScheduleIcon = withIcon(faClock);
const MailIcon = withIcon(faEnvelope);

const TABS = ['all', 'chats', 'shared', 'automations'] as const;
type Tab = (typeof TABS)[number];

const SNIPPET_LEAD = 24;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const contains = (text: string | null | undefined, query: string) => !!text && text.toLowerCase().includes(query);

const Highlight = ({ text, query }: { text: string; query: string }) => {
    if (!query) return <>{text}</>;
    const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'gi'));
    return (
        <>
            {parts.map((part, index) =>
                index % 2 === 1 ? (
                    <mark key={index} className="bg-surface-brand-weak text-content-strong">
                        {part}
                    </mark>
                ) : (
                    part
                )
            )}
        </>
    );
};

const snippet = (text: string, query: string) => {
    const flat = text.replace(/\s+/g, ' ').trim();
    const needle = query.replace(/\s+/g, ' ').trim().toLowerCase();
    const start = Math.max(0, flat.toLowerCase().indexOf(needle) - SNIPPET_LEAD);
    return `${start > 0 ? '…' : ''}${flat.slice(start)}`;
};

interface ResultRowProps {
    icon: React.ReactNode;
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    isActive?: boolean;
    onSelect: () => void;
}

const ResultRow = ({ icon, title, subtitle, isActive = false, onSelect }: ResultRowProps) => (
    <button
        type="button"
        onClick={onSelect}
        aria-current={isActive ? 'page' : undefined}
        className={classnames(
            'flex w-full cursor-pointer items-start gap-2 rounded-lg border-none px-2.5 py-2 text-left transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong',
            isActive ? 'bg-surface-brand-medium-default' : 'bg-transparent hover:bg-surface-medium'
        )}
    >
        <span aria-hidden className="flex-none pt-0.5 text-sm text-content-weak">
            {icon}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-sm font-medium text-content-strong">{title}</span>
            {subtitle && <span className="truncate text-xs text-content-weak">{subtitle}</span>}
        </span>
    </button>
);

const GroupHeader = ({ children }: { children: React.ReactNode }) => (
    <div className="px-2 pb-1 pt-3 text-xs font-medium text-content-weak">{children}</div>
);

export interface SidebarSearchResultsProps {
    query: string;
    conversations: any[];
    sharedConversations: any[];
    schedules: any[];
    triggers: any[];
    activeId?: string | null;
    onOpenChat: (conversationId: string) => void;
    onOpenSchedule: (scheduleId: string) => void;
    onOpenTrigger: (trigger: { id: string; agentId: string }) => void;
}

/** The sidebar's search results (ENG-11296): chats, shared chats and automations, matched in the browser against
 *  what the sidebar already loads — titles, names, prompts and instructions, never message text. */
const SidebarSearchResults = ({
    query: rawQuery,
    conversations,
    sharedConversations,
    schedules,
    triggers,
    activeId = null,
    onOpenChat,
    onOpenSchedule,
    onOpenTrigger,
}: SidebarSearchResultsProps) => {
    const { t } = useTranslation('chat');
    const [tab, setTab] = useState<Tab>('all');
    const query = rawQuery.trim();
    const needle = query.toLowerCase();

    const chats = conversations.filter(
        (conversation) => contains(conversation.title, needle) || contains(conversation.project?.agent?.name, needle)
    );
    const shared = sharedConversations.filter((share) => contains(share.snapshot?.title, needle));
    const matchedSchedules = schedules.filter(
        (schedule) => contains(schedule.name, needle) || contains(schedule.prompt, needle)
    );
    const matchedTriggers = triggers.filter(
        (trigger) => contains(trigger.name, needle) || contains(trigger.instruction, needle)
    );

    const chatRows = chats.map((conversation) => (
        <ResultRow
            key={`chat-${conversation.id}`}
            icon={<ChatIcon />}
            title={<Highlight text={conversation.title || t('sidebar.untitled')} query={query} />}
            subtitle={
                conversation.project?.agent?.name && <Highlight text={conversation.project.agent.name} query={query} />
            }
            isActive={conversation.id === activeId}
            onSelect={() => onOpenChat(conversation.id)}
        />
    ));
    const sharedRows = shared.map((share) => (
        <ResultRow
            key={`shared-${share.id}`}
            icon={<SharedIcon />}
            title={<Highlight text={share.snapshot.title || t('sidebar.sharedUntitled')} query={query} />}
            subtitle={t('sidebar.sharedFrom', {
                name: share.sharedBy ? displayName(share.sharedBy) : t('projects.admin'),
            })}
            isActive={share.snapshot.id === activeId}
            onSelect={() => onOpenChat(share.snapshot.id)}
        />
    ));
    const automationRows = [
        ...matchedSchedules.map((schedule) => (
            <ResultRow
                key={`schedule-${schedule.id}`}
                icon={<ScheduleIcon />}
                title={<Highlight text={schedule.name} query={query} />}
                subtitle={
                    contains(schedule.prompt, needle) ? (
                        <Highlight text={snippet(schedule.prompt, needle)} query={query} />
                    ) : (
                        formatCadence(t, schedule)
                    )
                }
                onSelect={() => onOpenSchedule(schedule.id)}
            />
        )),
        ...matchedTriggers.map((trigger) => (
            <ResultRow
                key={`trigger-${trigger.id}`}
                icon={<MailIcon />}
                title={<Highlight text={trigger.name} query={query} />}
                subtitle={
                    contains(trigger.instruction, needle) ? (
                        <Highlight text={snippet(trigger.instruction, needle)} query={query} />
                    ) : (
                        t('automations.onEmail')
                    )
                }
                onSelect={() => onOpenTrigger({ id: trigger.id, agentId: trigger.agentId })}
            />
        )),
    ];

    const groups: { key: Exclude<Tab, 'all'>; rows: React.ReactNode[] }[] = [
        { key: 'chats', rows: chatRows },
        { key: 'shared', rows: sharedRows },
        { key: 'automations', rows: automationRows },
    ];
    const visible = groups.filter((group) => (tab === 'all' || group.key === tab) && group.rows.length > 0);

    return (
        <div className="flex flex-col">
            <SegmentedButton
                value={tab}
                onValueChange={(value) => setTab(value as Tab)}
                aria-label={t('automations.search.tabsLabel')}
                className="mx-2 mb-1 flex self-stretch"
            >
                {TABS.map((key) => (
                    <SegmentedButton.Item
                        key={key}
                        value={key}
                        className="min-w-0 flex-1 justify-center px-1.5 text-xs"
                    >
                        <span className="truncate">{t(`automations.search.tabs.${key}`)}</span>
                    </SegmentedButton.Item>
                ))}
            </SegmentedButton>
            {visible.length === 0 ? (
                <p className="m-0 px-2.5 py-4 text-sm text-content-weak">{t('automations.search.noResults')}</p>
            ) : (
                visible.map((group) => (
                    <section key={group.key} aria-label={t(`automations.search.tabs.${group.key}`)}>
                        {tab === 'all' && <GroupHeader>{t(`automations.search.tabs.${group.key}`)}</GroupHeader>}
                        {group.rows}
                    </section>
                ))
            )}
        </div>
    );
};

export default SidebarSearchResults;
